import { useState, useCallback } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { RotateCcw, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const ZIKIR_LIST = [
  { id: 'subhanallah', text: 'سُبْحَانَ اللهِ', latin: 'Subhanallah', terjemah: 'Maha Suci Allah' },
  { id: 'alhamdulillah', text: 'الْحَمْدُ لِلَّهِ', latin: 'Alhamdulillah', terjemah: 'Segala Puji bagi Allah' },
  { id: 'allahu_akbar', text: 'اللهُ أَكْبَرُ', latin: 'Allahu Akbar', terjemah: 'Allah Maha Besar' },
  { id: 'astaghfirullah', text: 'أَسْتَغْفِرُ اللهَ', latin: 'Astaghfirullah', terjemah: 'Aku Memohon Ampun kepada Allah' },
];

export default function TasbihPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const TODAY = new Date().toISOString().split('T')[0];
  const storageKey = `tasbih_${user?.username || 'guest'}_${TODAY}`;

  const [selected, setSelected] = useState(ZIKIR_LIST[0]);
  const [counts, setCounts] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey)) || {}; }
    catch { return {}; }
  });
  const [pressed, setPressed] = useState(false);

  const current = counts[selected.id] || 0;
  const totalToday = Object.values(counts).reduce((s, v) => s + (v || 0), 0);

  const handleTap = useCallback(() => {
    if ('vibrate' in navigator) navigator.vibrate(30);
    setPressed(true);
    setTimeout(() => setPressed(false), 160);
    const newCounts = { ...counts, [selected.id]: (counts[selected.id] || 0) + 1 };
    setCounts(newCounts);
    localStorage.setItem(storageKey, JSON.stringify(newCounts));
  }, [counts, selected.id, storageKey]);

  const handleReset = () => {
    const newCounts = { ...counts, [selected.id]: 0 };
    setCounts(newCounts);
    localStorage.setItem(storageKey, JSON.stringify(newCounts));
    toast.success(`${selected.latin} direset`);
  };

  const isGold = current > 0 && current % 33 === 0;

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-emerald-950 via-emerald-900 to-emerald-800">
        {/* Header */}
        <div className="px-5 pt-12 pb-2 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 bg-white/10 rounded-xl text-white hover:bg-white/20 transition-colors">
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1 text-center">
            <h1 className="font-playfair text-xl font-bold text-white">Tasbih Digital</h1>
            <p className="text-emerald-300 text-xs mt-0.5">
              Total hari ini: <span className="font-bold text-amber-400">{totalToday}</span>
            </p>
          </div>
          <div className="w-10" />
        </div>

        {/* Zikir Selector */}
        <div className="px-5 mt-4 mb-5">
          <div className="grid grid-cols-2 gap-2">
            {ZIKIR_LIST.map(z => (
              <button key={z.id} data-testid={`zikir-${z.id}`} onClick={() => setSelected(z)}
                className={`py-3 px-3 rounded-2xl transition-all duration-200 text-center ${
                  selected.id === z.id
                    ? 'bg-amber-400 text-emerald-950 shadow-lg scale-105'
                    : 'bg-white/10 text-emerald-200 hover:bg-white/20'
                }`}>
                <span className="block text-base leading-tight mb-0.5" style={{ fontFamily: 'serif' }}>{z.text}</span>
                <span className="block text-xs font-semibold">{z.latin}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Main Counter */}
        <div className="flex flex-col items-center px-6">
          <AnimatePresence mode="popLayout">
            <motion.div key={current} initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 500, damping: 25 }}
              className="text-center mb-1">
              <p className="text-8xl font-bold tabular-nums leading-none"
                style={{ color: isGold ? '#F59E0B' : '#ffffff', textShadow: isGold ? '0 0 30px rgba(245,158,11,0.8)' : 'none' }}>
                {current}
              </p>
              {isGold && <p className="text-amber-400 text-xs mt-1 font-semibold animate-pulse">MasyaAllah! {current}x</p>}
            </motion.div>
          </AnimatePresence>
          <p className="text-emerald-300 text-sm mb-7">{selected.terjemah}</p>

          {/* Big Tap Button */}
          <motion.button
            data-testid="tasbih-tap-btn"
            onPointerDown={handleTap}
            animate={pressed ? { scale: 0.91 } : { scale: 1 }}
            transition={{ type: 'spring', stiffness: 600, damping: 20 }}
            className="w-56 h-56 rounded-full flex flex-col items-center justify-center select-none cursor-pointer relative"
            style={{
              background: pressed
                ? 'radial-gradient(circle at 35% 35%, #FCD34D 0%, #F59E0B 40%, #D97706 100%)'
                : 'radial-gradient(circle at 35% 35%, #34D399 0%, #059669 45%, #047857 100%)',
              boxShadow: pressed
                ? '0 0 50px 20px rgba(245,158,11,0.6), 0 0 100px 40px rgba(245,158,11,0.2), inset 0 2px 8px rgba(255,255,255,0.3)'
                : '0 0 35px 10px rgba(16,185,129,0.35), 0 10px 40px rgba(0,0,0,0.5), inset 0 2px 8px rgba(255,255,255,0.15)',
            }}
          >
            <span className="text-white/80 text-4xl leading-none mb-2" style={{ fontFamily: 'serif' }}>
              {selected.text.split(' ')[0]}
            </span>
            <span className="text-white font-bold text-sm">{selected.latin}</span>
            <span className="text-white/60 text-[10px] mt-1">Ketuk untuk menghitung</span>
          </motion.button>

          {/* Reset */}
          <button data-testid="tasbih-reset-btn" onClick={handleReset}
            className="mt-7 flex items-center gap-2 text-emerald-300 hover:text-amber-400 text-sm transition-colors py-2 px-5 rounded-xl hover:bg-white/10">
            <RotateCcw size={14} /> Reset {selected.latin}
          </button>
        </div>

        {/* Summary Card */}
        <div className="mx-5 mt-5 mb-10 bg-white/10 rounded-2xl p-4 backdrop-blur-sm">
          <h3 className="text-emerald-200 text-xs font-semibold mb-3 uppercase tracking-wider">Ringkasan Hari Ini</h3>
          <div className="grid grid-cols-2 gap-2">
            {ZIKIR_LIST.map(z => (
              <div key={z.id} className={`rounded-xl p-3 transition-all ${selected.id === z.id ? 'bg-amber-400/20 border border-amber-400/30' : 'bg-white/10'}`}>
                <p className="text-[10px] text-emerald-300">{z.latin}</p>
                <p className={`font-bold text-xl ${selected.id === z.id ? 'text-amber-400' : 'text-white'}`}>
                  {counts[z.id] || 0}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
            <span className="text-emerald-300 text-xs">Total Semua Zikir</span>
            <span className="text-amber-400 font-bold text-lg">{totalToday}</span>
          </div>
        </div>
      </div>
    </Layout>
  );
}
